// pages/rental/PaymentResult.jsx — landing page for PayU redirects
// PayU sends the customer's browser back to our backend's
// /api/payment/payu/{success,failure,cancel} endpoints, which verify the
// payment server-side and then redirect here:
//   https://vegatas.com/payment-success?transactionId=...
//   https://vegatas.com/payment-failure?transactionId=...&reason=...
//   https://vegatas.com/payment-cancel?transactionId=...
// This page never talks to PayU directly — it just reflects the outcome
// the backend already decided, and helps the user get to their next step.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { bookingService } from '../../services/bookingService';
import { checkUserDocuments, getCurrentUserId } from '../../utils/documentCheck';

const T = {
  ink: '#0D0D0F',
  slate: '#2D2D38',
  muted: '#6B6B7A',
  line: '#E8E8F0',
  surface: '#F5F5FA',
  white: '#FFFFFF',
  lime: '#C8F23C',
  teal: '#00BFA5',
  coral: '#FF6D3A',
  amber: '#F59E0B',
  red: '#ef4444',
};

const STATUS_CONFIG = {
  success: {
    icon: '✓',
    iconBg: T.lime,
    iconColor: T.ink,
    title: 'Payment Successful',
    subtitle: "Your booking is confirmed. We've sent the details to your account.",
    accent: T.lime,
  },
  failure: {
    icon: '✕',
    iconBg: T.red,
    iconColor: T.white,
    title: 'Payment Failed',
    subtitle: "Your payment couldn't be completed, so no booking was created. No amount has been charged.",
    accent: T.red,
  },
  cancel: {
    icon: '!',
    iconBg: T.amber,
    iconColor: T.white,
    title: 'Payment Cancelled',
    subtitle: "You cancelled the payment before it completed. No booking was created and nothing was charged.",
    accent: T.amber,
  },
};

const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

export default function PaymentResult({ status = 'success' }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.failure;

  // Accept a few common param name variants defensively — backend team may
  // send PayU's raw field names through instead of a clean transactionId.
  const transactionId = searchParams.get('transactionId') || searchParams.get('txnid') || searchParams.get('mihpayid');
  const bookingId = searchParams.get('bookingId');
  const bikeId = searchParams.get('bikeId');
  const amount = searchParams.get('amount');
  const reason = searchParams.get('reason') || searchParams.get('message') || searchParams.get('error');

  const [booking, setBooking] = useState(null);
  const [bookingLoadFailed, setBookingLoadFailed] = useState(false);
  const [docsChecked, setDocsChecked] = useState(false);
  const [needsDocs, setNeedsDocs] = useState(false);
  const [countdown, setCountdown] = useState(6);

  // Best-effort: if the redirect included a bookingId, fetch it to show a summary.
  useEffect(() => {
    let cancelled = false;
    if (!bookingId) return;

    bookingService.getBookingById(bookingId)
      .then((data) => { if (!cancelled) setBooking(data); })
      .catch(() => { if (!cancelled) setBookingLoadFailed(true); });

    return () => { cancelled = true; };
  }, [bookingId]);

  // On success, check whether the user still needs to upload KYC documents.
  useEffect(() => {
    if (status !== 'success') return;
    let cancelled = false;

    checkUserDocuments(getCurrentUserId(user)).then((hasDocuments) => {
      if (cancelled) return;
      setNeedsDocs(!hasDocuments);
      setDocsChecked(true);
    });

    return () => { cancelled = true; };
  }, [status, user]);

  const goNext = useMemo(() => () => {
    if (needsDocs) {
      navigate('/document-verification', {
        state: { fromBooking: true, bookingId, message: 'Please upload your documents to complete the booking process' }
      });
    } else {
      navigate('/rental/search/my-bookings', { state: { showSuccess: true, paymentSuccess: true }, replace: true });
    }
  }, [needsDocs, bookingId, navigate]);

  // Auto-advance countdown once we know where to send the user.
  useEffect(() => {
    if (status !== 'success' || !docsChecked) return;
    if (countdown <= 0) {
      goNext();
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, docsChecked, countdown, goNext]);

  const handleRetry = () => {
    if (bikeId) navigate(`/rental/bike/${bikeId}`);
    else navigate('/rental/search');
  };

  return (
    <div style={{ minHeight: '100vh', background: T.surface, fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&family=Space+Grotesk:wght@700&display=swap');
        * { box-sizing: border-box; }
        @keyframes popIn { from { opacity: 0; transform: scale(.85); } to { opacity: 1; transform: none; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
      `}</style>

      <div style={{ width: '100%', maxWidth: 460, animation: 'fadeUp .4s ease' }}>
        <div style={{ background: T.white, borderRadius: 22, border: `1.5px solid ${T.line}`, boxShadow: '0 12px 40px rgba(0,0,0,.08)', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ background: T.ink, padding: '36px 28px 28px', textAlign: 'center' }}>
            <div style={{
              width: 68, height: 68, borderRadius: '50%', margin: '0 auto 18px',
              background: cfg.iconBg, color: cfg.iconColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 32, fontWeight: 900, animation: 'popIn .4s cubic-bezier(.16,1,.3,1)',
            }}>
              {cfg.icon}
            </div>
            <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 21, fontWeight: 700, color: T.white, marginBottom: 8 }}>
              {cfg.title}
            </h1>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,.55)', lineHeight: 1.6, maxWidth: 340, margin: '0 auto' }}>
              {cfg.subtitle}
            </p>
          </div>

          <div style={{ padding: '24px 28px 28px' }}>
            {/* Transaction / reason details */}
            {(transactionId || reason) && (
              <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 14, padding: '14px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {transactionId && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .6 }}>Transaction ID</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.ink, wordBreak: 'break-all', textAlign: 'right' }}>{transactionId}</span>
                  </div>
                )}
                {amount && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .6 }}>Amount</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>₹{amount}</span>
                  </div>
                )}
                {reason && status !== 'success' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .6 }}>Reason</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.ink, textAlign: 'right' }}>{reason}</span>
                  </div>
                )}
              </div>
            )}

            {/* Booking summary, if we could load it */}
            {booking && !bookingLoadFailed && (
              <div style={{ border: `1.5px solid ${T.line}`, borderRadius: 14, padding: '16px', marginBottom: 16 }}>
                <p style={{ fontSize: 10, fontWeight: 800, color: T.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Booking Summary</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: T.slate }}>Booking</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>#{booking.bookingId || booking.id}</span>
                  </div>
                  {(booking.startDate1 || booking.startDate) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, color: T.slate }}>Pickup</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{fmtDate(booking.startDate1 || booking.startDate)}</span>
                    </div>
                  )}
                  {(booking.endDate1 || booking.endDate) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, color: T.slate }}>Drop-off</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{fmtDate(booking.endDate1 || booking.endDate)}</span>
                    </div>
                  )}
                  {booking.finalAmount != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, color: T.slate }}>Amount Paid</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: T.ink }}>₹{Number(booking.finalAmount).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            {status === 'success' ? (
              <>
                <button onClick={goNext}
                  style={{ width: '100%', padding: '15px', borderRadius: 14, border: 'none', background: T.ink, color: T.lime, fontSize: 14, fontWeight: 900, cursor: 'pointer', marginBottom: 10 }}>
                  {needsDocs ? '📄 Upload Documents' : '📋 Go to My Bookings'}
                  {docsChecked && ` (${countdown}s)`}
                </button>
                <Link to="/rental/search" style={{ display: 'block', textAlign: 'center', fontSize: 12, fontWeight: 700, color: T.muted, textDecoration: 'none' }}>
                  Continue browsing bikes
                </Link>
              </>
            ) : (
              <>
                <button onClick={handleRetry}
                  style={{ width: '100%', padding: '15px', borderRadius: 14, border: 'none', background: T.ink, color: T.lime, fontSize: 14, fontWeight: 900, cursor: 'pointer', marginBottom: 10 }}>
                  🔁 Try Payment Again
                </button>
                <Link to="/rental/search/my-bookings" style={{ display: 'block', textAlign: 'center', fontSize: 12, fontWeight: 700, color: T.muted, textDecoration: 'none' }}>
                  View My Bookings
                </Link>
              </>
            )}
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: T.muted, marginTop: 16 }}>
          Need help? Contact our support team and reference the transaction ID above.
        </p>
      </div>
    </div>
  );
}
